<template>
    <div class="spotlight-widget spotlight-exchange-widget" :class="{ 'spotlight-widget--clickable': !error && !loading }" @click="copyResult">
        <template v-if="loading">
            <div class="spotlight-widget-icon spotlight-widget-icon--exchange">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            </div>
            <div class="spotlight-widget-content">
                <div class="spotlight-widget-result spotlight-widget-result--loading">...</div>
            </div>
        </template>
        <template v-else-if="error">
            <div class="spotlight-widget-icon spotlight-widget-icon--error">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div class="spotlight-widget-content">
                <div class="spotlight-widget-error-text">{{ error }}</div>
            </div>
        </template>
        <template v-else>
            <div class="spotlight-widget-icon spotlight-widget-icon--exchange">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            </div>
            <div class="spotlight-widget-content">
                <div class="spotlight-widget-result">{{ formattedInput }} <span class="spotlight-widget-arrow">→</span> {{ formattedOutput }}</div>
                <div class="spotlight-widget-expression">1 {{ query.from }} = {{ unitRate }} {{ displayTo }}</div>
            </div>
            <div v-if="copied" class="spotlight-widget-copied">{{ $t('common.spotlight.copied') }}</div>
        </template>
    </div>
</template>

<script>
import { CURRENCY_SYMBOLS, fetchRates, formatCurrency } from './currencyRates'

export default {
    name: 'ExchangeCalculator',
    props: {
        query: { type: Object, required: true }
    },
    data() {
        return {
            rates: null,
            loading: false,
            error: null,
            copied: false
        }
    },
    computed: {
        displayTo() {
            return this.query.to || this.defaultCurrency
        },
        defaultCurrency() {
            return this.$root.defaultCurrency?.code || 'TRY'
        },
        convertedAmount() {
            if (!this.rates) return null
            const from = this.query.from
            const to = this.displayTo
            if (from === to) return this.query.amount
            const fromRate = this.rates[from]
            const toRate = this.rates[to]
            if (!fromRate || !toRate) return null
            return this.query.amount * (toRate / fromRate)
        },
        unitRateValue() {
            if (!this.rates) return null
            const from = this.query.from
            const to = this.displayTo
            const fromRate = this.rates[from]
            const toRate = this.rates[to]
            if (!fromRate || !toRate) return null
            return toRate / fromRate
        },
        unitRate() {
            if (this.unitRateValue === null) return ''
            return this.formatNumber(this.unitRateValue)
        },
        formattedInput() {
            const sym = CURRENCY_SYMBOLS[this.query.from] || this.query.from
            return sym + this.formatNumber(this.query.amount)
        },
        formattedOutput() {
            if (this.convertedAmount === null) return '—'
            const sym = CURRENCY_SYMBOLS[this.displayTo] || this.displayTo
            return this.convertedAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + sym
        }
    },
    watch: {
        'query.from': { handler: 'fetchRates', immediate: true },
        'query.to': 'fetchRates'
    },
    methods: {
        formatNumber(val) {
            if (Number.isInteger(val)) return val.toLocaleString('tr-TR')
            return val.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
        },
        async fetchRates() {
            this.loading = true
            this.error = null
            try {
                this.rates = await fetchRates(this.axios, this.endpoints.currency)
            } catch (e) {
                this.error = this.$t('common.spotlight.currencyError')
            } finally {
                this.loading = false
            }
        },
        async copyResult() {
            if (this.error || this.loading || this.convertedAmount === null) return
            try {
                await navigator.clipboard.writeText(String(this.convertedAmount.toFixed(2)))
                this.copied = true
                setTimeout(() => { this.copied = false }, 1500)
            } catch (e) { void e }
        }
    }
}
</script>
